import Modal from '@components/v2/ui/Modal/Modal';
import { Box } from '@mui/material';
import useHandleBrandContextInSessionStorage from '../../../../hooks/useHandleBrandContextInSessionStorage';
import LangEnum from '../../../../lang/LangEnum';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { UsedInOfflineMode } from '../../../offline/types/UsedInOfflineMode';
import { useTranslation } from '../../../translation/TranslationContext';
import { useOrderActionsContext } from '../../contexts/OrderActions.context';
import { ExportOrderScope } from './ExportOrderModal.types';
import ExportOrderTabs from './components/ExportOrderTabs';
import OrderCsvContent from './components/OrderCsvContent';
import OrderPdfContent from './components/OrderPdfContent';
import useBrandSettingsForStoreExport from './hooks/useBrandSettingsForStoreExport';
import { useExportOrder } from './hooks/useExportOrder';
import { EXPORT_ORDER_MODAL_HEIGHT, PDF } from './utils/exportOrder.consts';
import { exportOrderModalTitle, primaryActionButton } from './utils/exportOrder.utils';

export interface ExportOrderModalProps extends UsedInOfflineMode {
  open: boolean;
  onClose: () => void;
  exportScope: ExportOrderScope;
  openVerupModal?: () => void;
}

const ExportOrderModal = ({
  open,
  onClose: handleClose,
  isOfflineMode = false,
  exportScope,
  openVerupModal,
}: ExportOrderModalProps) => {
  const { hasVerupIntegration } = useApplicationContext();
  const { orderRaw } = useOrderActionsContext();
  const { translate } = useTranslation();

  useHandleBrandContextInSessionStorage({ brandId: orderRaw?.brandId, enabled: open });
  useBrandSettingsForStoreExport({ brandId: orderRaw?.brandId, enabled: open, isOfflineMode });

  const onClose = () => {
    handleClose();
  };

  const { control, submit, tabContent, setTabContent, isSubmitting, formMethods } = useExportOrder({
    hasVerupIntegration,
    openVerupModal,
    onClose,
    exportScope,
    translate,
    isOfflineMode,
    orderRaw,
  });

  return (
    <Modal
      isOpen={open}
      size="standard"
      height={EXPORT_ORDER_MODAL_HEIGHT}
      header={{ title: translate(exportOrderModalTitle(exportScope)) }}
      onClose={onClose}
      footer={{
        primaryAction: {
          onClick: submit,
          label: translate(primaryActionButton(exportScope, tabContent)),
          isLoading: isSubmitting,
        },
        secondaryAction: {
          onClick: onClose,
          label: translate(LangEnum.GLOBAL_CANCEL_LABEL),
        },
      }}
      slots={{
        headerBottom: (
          <ExportOrderTabs isOffline={isOfflineMode} onChange={setTabContent} value={tabContent} />
        ),
      }}
    >
      <Box sx={{ pt: 0.75 }}>
        {tabContent === PDF ? (
          <OrderPdfContent
            control={control}
            formMethods={formMethods}
            asyncExport={!exportScope.isSyncExport}
          />
        ) : (
          <OrderCsvContent
            control={control}
            isModelOptionVisible={!isOfflineMode}
            isVerupOptionVisible={hasVerupIntegration && !isOfflineMode}
          />
        )}
      </Box>
    </Modal>
  );
};

export default ExportOrderModal;
